import { ContactPaymentInstrument as NewContactPaymentInstrument } from '@node-space/types'
import { BeneficiaryTransferDestination } from './beneficiaries'
import { WithPaginationApiResponseExtended } from './pagination'
import { PaymentContextType } from './payments'

export const ContactEntity = {
  COMPANY: 'COMPANY',
  INDIVIDUAL: 'INDIVIDUAL',
} as const

export type ContactEntityType = (typeof ContactEntity)[keyof typeof ContactEntity]

export const RelationshipType = {
  THIRD_PARTY: 'THIRD_PARTY',
  SELF_OWNED: 'SELF_OWNED',
} as const

export type RelationshipType = (typeof RelationshipType)[keyof typeof RelationshipType]

export const InstrumentType = {
  CRYPTO: 'CRYPTO',
  BANK_ACCOUNT: 'BANK_ACCOUNT',
} as const

export type InstrumentType = (typeof InstrumentType)[keyof typeof InstrumentType]

export const GovernmentIdType = {
  CUIL: 'CUIL',
  CPF: 'CPF',
  CUIT: 'CUIT',
  CNPJ: 'CNPJ',
} as const

export type GovernmentIdType = (typeof GovernmentIdType)[keyof typeof GovernmentIdType]

export interface GovernmentId {
  type: GovernmentIdType | undefined
  number: string
  issuingCountry: string
  issueDate?: string
  expiryDate?: string
  rowId?: string
}

export interface ContactEntity {
  category?: string
  type: ContactEntityType
  address?: Address
  governmentIds?: GovernmentId[]
}

export interface ContactEntityIndividual extends ContactEntity {
  firstName: string
  lastName: string
  dateOfBirth: string
}

export interface ContactEntityCompany extends ContactEntity {
  legalName: string
  registrationNumber: string
  dateOfIncorporation?: string
}

export interface ContactPayload {
  description: string
  relationshipType: RelationshipType
  entity: ContactEntityIndividual | ContactEntityCompany
}

export interface Contact extends ContactPayload {
  id: string
  name: string
  defaultReference: string
  updatedAt?: Date | string
  createdAt?: Date | string
}

export interface ContactCaseCount {
  count: number
}

export type RequiredInformation = 'CONTACT_REQUIRED' | 'INSTRUMENT_VERIFICATION_REQUIRED'
export interface ContactCase {
  externalId: string
  hash: string
  currencyCode: string
  network: string
  amount: number
  paymentCreated: Date
  paymentContext: ContactCasePaymentContext
  requiredInformation: RequiredInformation[]
}

export interface ContactCasePaymentContext {
  paymentExternalId?: string
  type: PaymentContextType
  channelUUID?: string
  withdrawalExternalReference?: string // present when type === WITHDRAWAL
  userReference?: string // present when type === WITHDRAWAL
}

export interface Address {
  addressLine1?: string
  addressLine2?: string
  city?: string
  region?: string
  regionCode?: string
  stateCode?: string
  postCode?: string
  country: string
}

export interface IntermediaryBank {
  name: string
  identificationCode?: string
  swiftCode?: string
  nid?: string
  bic?: string
  identificationCodeType?: IdentificationCodeType
  identificationCodeValue?: string
  address?: Address
}

export interface ContactInstrumentAccountHolderName {
  firstName?: string
  lastName?: string
  legalName?: string
}

export interface ContactInstrument {
  // Common fields
  type: InstrumentType
  contact?: Contact
  provider?: string
  transferDestination?: string
  updatedAt?: string
  createdAt?: string
  verification?: {
    type: VerificationType
    status: VerificationStatus
  }
  id: string
  currency: string
  description: string
  // Crypto fields
  address?: string
  tag?: string
  network?: string
  selfHosted?: boolean
  // Bank account fields
  accountNumber?: string
  accountType?: string
  paymentMethods?: string[]
  bank?: ContactBankAccountBank
  accountHolderName?: ContactInstrumentAccountHolderName
  accountHolderAddress?: Address
  intermediaryBanks?: IntermediaryBank[]
  defaultRemittanceInformation?: string
}

export type ContactInstruments = WithPaginationApiResponseExtended<ContactInstrument[]>

export type Contacts = WithPaginationApiResponseExtended<Contact[]>

export const VerificationType = {
  NONE: 'NONE',
  SCREENSHOT: 'SREENSHOT',
} as const

export type VerificationType = (typeof VerificationType)[keyof typeof VerificationType]

export const VerificationStatus = {
  UNVERIFIED: 'UNVERIFIED',
  VERIFICATION_REQUESTED: 'VERIFICATION_REQUESTED',
  VERIFIED: 'VERIFIED',
} as const

export type VerificationStatus = (typeof VerificationStatus)[keyof typeof VerificationStatus]

export interface ContactsRequest {
  size?: number
  page?: number
  searchQuery?: string
  contactIds?: string[]
  contactId?: string
  type?: ContactEntityType
  totalCountLimit?: number
}

export interface ContactV3Address {
  addressLine1: string
  addressLine2?: string
  city: string
  postalCode: string
  region?: string
  stateCode?: string
  country: string
}

interface ContactV3EntityBase {
  relationshipType: RelationshipType
  address?: ContactV3Address
  governmentIds?: GovernmentId[]
}

export interface ContactV3IndividualEntity extends ContactV3EntityBase {
  type: 'INDIVIDUAL'
  firstName: string
  middleName?: string
  lastName: string
  dateOfBirth?: string
}

export interface ContactV3CompanyEntity extends ContactV3EntityBase {
  type: 'COMPANY'
  legalName: string
  registrationNumber?: string
  dateOfIncorporation?: string
}

export type ContactV3Entity = ContactV3IndividualEntity | ContactV3CompanyEntity

export interface ContactV3 {
  id: string
  description?: string
  entity: ContactV3Entity
  createdAt: string
  updatedAt: string
}

export interface ContactsV3Response {
  content: ContactV3[]
  pageable: {
    pageNumber: number
    pageSize: number
  }
  hasNext: boolean
}

export interface ContactsV3Request {
  size?: number
  page?: number
  searchQuery?: string
}

export interface ContactInstrumentPayload {
  type: InstrumentType
  address: string
  network: string
  currency: string
  description: string
}

export interface ContactInstrumentProps {
  instrumentId?: string
  contactId: string
  instrument?: ContactInstrumentPayload | CreateContactBankAccountRequest
}

export interface PutContactProps {
  contactId: string
  contact: ContactPayload
}

export type ContactPaymentInstrument = {
  id: string
  currency: string
  description: string
  updatedAt: string
  createdAt: string
  address: string
  tag?: string | null
  network: string
  type: InstrumentType
}

export type ContactPaymentInstruments = {
  paymentInstrument: CryptoPaymentInstrument | BankAccountPaymentInstrument
  contact: Contact
  eligibility: WalletContactInstrumentEligibility[]
}

export interface ContactPaymentInstrumentsResponse extends WithPaginationApiResponseExtended<
  NewContactPaymentInstrument[]
> {}

export interface PostContactCase {
  contactId: string
  externalId: string
}

export const IdentificationCodeTypes = {
  BIC: 'BIC',
  NID: 'NID',
} as const

export type IdentificationCodeType =
  (typeof IdentificationCodeTypes)[keyof typeof IdentificationCodeTypes]

// Bank account instruments
interface ContactBankAccountBank {
  name: string
  identificationCode?: string
  identificationCodeType?: IdentificationCodeType
  identificationCodeValue?: string
  swiftCode?: string
  nid?: string
  bic?: string
  address: Address
}

export interface AccountHolderName {
  firstName?: string
  lastName?: string
  legalName?: string
}
export interface CreateContactBankAccountRequest {
  currency: string
  description: string
  defaultRemittanceInformation?: string
  destinations?: BeneficiaryTransferDestination[]
  accountNumber: string
  accountHolderName: AccountHolderName
  accountHolderAddress: Address
  bank: ContactBankAccountBank
  paymentMethods: string[]
  intermediaryBanks?: IntermediaryBank[]
  type: InstrumentType
}

export interface CreateContactBankAccountResponse {}

export interface CreateContactBankAccountInstrumentProps {
  contactId: string
  request?: CreateContactBankAccountRequest
}

export const ContactInstrumentDecisionStep = {
  CONTACT: 'CONTACT',
  CONTACT_DECISION: 'CONTACT_DECISION',
  INSTRUMENT: 'INSTRUMENT',
} as const

export type ContactInstrumentDecisionStep =
  (typeof ContactInstrumentDecisionStep)[keyof typeof ContactInstrumentDecisionStep]

interface BasePaymentInstrument {
  id: string
  currency: string
  description: string
  updatedAt: string
  createdAt: string
  type: InstrumentType
}
interface CryptoPaymentInstrument extends BasePaymentInstrument {
  address: string
  tag?: string | null
  network: string
}

export interface BankAccountPaymentInstrument extends BasePaymentInstrument {
  accountNumber: string
  accountType?: string
  intermediaryBanks?: IntermediaryBank[]
  bank: ContactBankAccountBank
  accountHolderName: AccountHolderName
  accountHolderAddress: Address
  paymentMethods: string[]
  defaultRemittanceInformation?: string
}

export const EligibilityStatus = {
  ELIGIBLE: 'ELIGIBLE',
  INELIGIBLE: 'INELIGIBLE',
} as const

export type EligibilityStatus = (typeof EligibilityStatus)[keyof typeof EligibilityStatus]

export interface WalletContactInstrumentEligibility {
  paymentMethod: string
  status: EligibilityStatus
  reasons: string[]
}

interface WalletBasePaymentInstrument {
  id: string
  currency: string
  description: string
  updatedAt: string
  createdAt: string
  type: InstrumentType
  verification: {
    type: VerificationType
    status: VerificationStatus
  }
}

export interface WalletCryptoPaymentInstrument extends WalletBasePaymentInstrument {
  address: string
  tag: string | null
  network: string
  selfHosted: boolean
}

export interface WalletBankAccountPaymentInstrument extends WalletBasePaymentInstrument {
  accountNumber: string
  accountType: string | null
  paymentMethods: string[]
  intermediaryBanks: IntermediaryBank[]
  bank: ContactBankAccountBank
  accountHolderName: ContactInstrumentAccountHolderName
  accountHolderAddress: Address
}

export interface WalletContactInstrumentContact {
  id: string
  name: string
  description: string
  relationshipType: RelationshipType
  entity: ContactEntityCompany | ContactEntityIndividual
  updatedAt: string
  createdAt: string
}

export interface WalletContactInstrumentsResponse {
  totalElements: number
  content: ContactPaymentInstruments[]
  pageable: {
    pageNumber: number
    pageSize: number
  }
}

export interface PaymentMethodSupport {
  paymentMethod: string
  supported: boolean
}
